/**
 * App Layout Types
 *
 * TypeScript interfaces for the app layout system.
 */

import type { LucideIcon } from 'lucide-react';

/**
 * Navigation item configuration
 */
export interface NavigationItem {
  /** Unique identifier */
  id: string;
  /** i18n translation key for the label */
  labelKey: string;
  /** Fallback label if translation not found */
  label: string;
  /** Lucide icon component */
  icon: LucideIcon;
  /** Route path */
  path: string;
  /** Required permission to view this item */
  permission?: string;
  /**
   * Hide this item for these business types. Use uppercase values matching
   * Business.businessType on the backend (e.g. 'BROKER', 'AGENT'). Items
   * with no value are shown to everyone.
   */
  hideForBusinessTypes?: string[];
  /** Badge count (e.g., notifications) */
  badge?: number;
  /** Child navigation items */
  children?: NavigationItem[];
  /** Whether to show only in mobile bottom nav */
  mobileOnly?: boolean;
  /** Whether to hide from mobile bottom nav */
  hideFromBottomNav?: boolean;
}

/**
 * Navigation section grouping
 */
export interface NavigationSection {
  /** Section identifier */
  id: string;
  /** i18n translation key for section title */
  titleKey?: string;
  /** Navigation items in this section */
  items: NavigationItem[];
}

/**
 * Props for the main app layout
 */
export interface AppLayoutProps {
  children: React.ReactNode;
}

/**
 * Props for sidebar component
 */
export interface SidebarProps {
  className?: string;
}

/**
 * Props for individual nav item
 */
export interface NavItemProps {
  item: NavigationItem;
  isActive: boolean;
  isCollapsed: boolean;
  onClick?: () => void;
}

/**
 * Props for mobile drawer
 */
export interface MobileDrawerProps {
  isOpen: boolean;
  onClose: () => void;
}

/**
 * Props for bottom navigation
 */
export interface BottomNavProps {
  className?: string;
}

/**
 * Drawer identifiers for useUIStore
 */
export const DRAWER_IDS = {
  MOBILE_NAV: 'mobile-nav',
  USER_MENU: 'user-menu',
} as const;

/**
 * Modal identifiers for useUIStore
 */
export const MODAL_IDS = {
  SEARCH: 'search',
  NOTIFICATIONS: 'notifications',
} as const;
